/**
 * L1J Web Client - Core Types
 * 세션 및 상태 관리 모듈에서 사용되는 타입 정의
 */

/**
 * 세션 상태 열거형
 */
export enum SessionState {
  /** WebSocket 연결됨, 미인증 */
  CONNECTED = 'CONNECTED',
  /** 로그인 성공, 암호화 활성화됨 */
  AUTHENTICATED = 'AUTHENTICATED',
  /** 캐릭터 선택 완료, 게임 중 */
  IN_GAME = 'IN_GAME',
}

/**
 * 캐릭터 정보
 */
export interface CharacterInfo {
  name: string;
  clanName: string;
  type: number;
  sex: number;
  lawful: number;
  hp: number;
  mp: number;
  level?: number;
  x?: number;
  y?: number;
  mapId?: number;
}

/**
 * 세션 데이터
 */
export interface Session {
  state: SessionState;
  account: string | null;
  charName: string | null;
  charList: CharacterInfo[];
  loginTime: number | null;
}

/**
 * 엔티티 기본 인터페이스
 */
export interface Entity {
  objId: number;
  x: number;
  y: number;
  gfxId: number;
  heading: number;
  hpPercent?: number;
}

/**
 * 플레이어 엔티티
 */
export interface Player extends Entity {
  account: string;
  charName: string;
  level?: number;
  hp?: number;
  mp?: number;
  maxHp?: number;
  maxMp?: number;
}

/**
 * NPC 엔티티
 */
export interface Npc extends Entity {
  npcId: number;
  name: string;
}

/**
 * 몬스터 엔티티
 */
export interface Monster extends Entity {
  monsterId: number;
  name: string;
  level: number;
}

/**
 * 아이템 인터페이스
 */
export interface Item {
  id: number;
  gfxId: number;
  count: number;
  name: string;
  identified: boolean;
  enchantLevel: number;
  /**
   * 서버 L1Item.getUseType(). 서버 C_UseItem이 이 값으로 itemObjId 뒤에 읽을
   * 인자의 형태를 정하므로, use_item을 보낼 때 함께 실어야 패킷 길이가 맞는다.
   * 게이트웨이가 값을 주지 않은 옛 경로를 위해 선택 필드로 둔다.
   */
  useType?: number;
}

/**
 * 채팅 메시지
 */
export interface ChatMessage {
  type: string;
  sender: string;
  message: string;
  timestamp: number;
}

/**
 * 세계관 상태
 */
export interface WorldState {
  mapId: number;
  players: Map<number, Player>;
  npcs: Map<number, Npc>;
  monsters: Map<number, Monster>;
}

/**
 * 게임 상태
 */
export interface GameState {
  playerObjId: number | null;
  playerX: number;
  playerY: number;
  playerHeading: number;
  hp: number;
  mp: number;
  maxHp: number;
  maxMp: number;
  level: number;
  mapId: number;
  inventory: Item[];
  chatMessages: ChatMessage[];
}

/**
 * 스토리지 데이터 구조
 */
export interface StorageData {
  session: {
    account: string | null;
    lastLogin: number | null;
  };
  preferences: {
    audioEnabled: boolean;
    musicVolume: number;
    sfxVolume: number;
  };
}

/**
 * S_OwnCharStatus(opcode 145)가 싣고 오는 자기 캐릭터 상세 정보.
 *
 * 게이트웨이 ProtocolTranslator.OwnCharStatusData와 같은 구성이다.
 * HP/MP/레벨은 StatusBar가 쓰는 요약 스탯과 겹치지만, 상세 창은 이 묶음
 * 하나만 보면 되도록 함께 담는다.
 */
export interface PlayerDetailStats {
  level: number;
  exp: number;
  str: number;
  intel: number;
  wis: number;
  dex: number;
  con: number;
  cha: number;
  currentHp: number;
  maxHp: number;
  currentMp: number;
  maxMp: number;
  ac: number;
  /** 만복도 (0~225). */
  food: number;
  /** 인벤토리 무게 (0~240 스케일). */
  weight: number;
  lawful: number;
  /**
   * 마법 공격력 보정(SP)과 마법 저항(MR).
   *
   * S_OwnCharStatus 스냅샷에는 들어 있지 않고 **S_SpMr(opcode 174)로만** 온다.
   * 그래서 스냅샷을 다시 받아도 이 두 값은 이전 값을 이어받는다
   * (GameState의 own_char_status 브리지 참조). 아직 못 받았으면 0이다.
   */
  sp: number;
  mr: number;
  /** 속성 저항. */
  fire: number;
  water: number;
  wind: number;
  earth: number;
  monsterKill: number;
}
